import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Eye, Trash2, LogOut, Search, FileText, BookCheck, FileEdit, Clock, KeyRound, Users, Radio, Loader2, CheckSquare, Square, Upload, Download, MessageSquare, BarChart3, Briefcase } from "lucide-react";
import { adminFetchPosts, adminDeletePost, adminBulkAction, adminSitemapPing, isAdminAuthed, clearAdminToken, CATEGORY_COLOR } from "@/lib/blogApi";
import ChangePasswordModal from "@/components/admin/ChangePasswordModal";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const authed = isAdminAuthed();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("publishedAt");
  const [sortDir, setSortDir] = useState("desc");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showChangePw, setShowChangePw] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const refresh = () => {
    setLoading(true);
    adminFetchPosts()
      .then(setPosts)
      .catch(() => toast.error("Failed to load posts"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (authed) refresh(); }, [authed]);

  const logout = () => { clearAdminToken(); navigate("/admin"); };

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    const list = posts.filter((p) =>
      !term ||
      p.title.toLowerCase().includes(term) ||
      (p.tags || []).some((t) => t.toLowerCase().includes(term))
    );
    list.sort((a, b) => {
      const va = (a[sortKey] ?? "").toString();
      const vb = (b[sortKey] ?? "").toString();
      return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return list;
  }, [posts, search, sortKey, sortDir]);

  const stats = useMemo(() => {
    const published = posts.filter((p) => p.isPublished).length;
    const drafts = posts.length - published;
    const totalRead = posts.reduce((s, p) => s + (p.readingTime || 0), 0);
    return { total: posts.length, published, drafts, totalRead };
  }, [posts]);

  const filteredIds = useMemo(() => filtered.map((p) => p.id), [filtered]);
  const allOnPageSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));

  if (!authed) return <Navigate to="/admin" replace />;

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await adminDeletePost(confirmDelete.id);
      toast.success("Post deleted");
      setConfirmDelete(null);
      refresh();
    } catch { toast.error("Delete failed"); }
  };

  // ---- bulk selection ----
  const toggleAll = () => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (allOnPageSelected) filteredIds.forEach((id) => next.delete(id));
      else filteredIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const toggleOne = (id) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const res = await adminBulkAction(Array.from(selected), action);
      const verb = action === "delete" ? "deleted" : action === "publish" ? "published" : "unpublished";
      toast.success(`${res.affected} post${res.affected === 1 ? "" : "s"} ${verb}`);
      clearSelection();
      refresh();
      setConfirmBulkDelete(false);
    } catch { toast.error(`Bulk ${action} failed`); }
    finally { setBulkBusy(false); }
  };

  const doPing = async () => {
    setPinging(true);
    try {
      const res = await adminSitemapPing();
      if (res.ok) {
        toast.success(`Pinged IndexNow: ${res.submitted}/${res.total} URLs submitted (Bing, Yandex, Seznam, Naver, Yep).`);
      } else if (res.skipped) {
        toast.error(`IndexNow skipped: ${res.reason}`);
      } else {
        const failure = (res.failures && res.failures[0]) || {};
        toast.error(`IndexNow ping failed: ${failure.status || failure.error || "unknown error"}`);
      }
    } catch (e) {
      toast.error("Ping failed");
    } finally { setPinging(false); }
  };

  return (
    <div className="pt-[72px] bg-brand-cream/40 min-h-screen" data-testid="page-admin-dashboard">
      <div className="container-wide py-10">
        {/* Top bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <p className="eyebrow mb-2">◦  Spiruva Admin</p>
            <h1 className="font-display text-4xl text-brand-black">Editorial Dashboard</h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/admin/posts/new" data-testid="admin-new-post" className="btn-primary">
              <Plus size={14} /> New Post
            </Link>
            <Link to="/admin/leads" data-testid="admin-nav-leads" className="btn-secondary">
              <Users size={14} /> Leads
            </Link>
            <Link to="/admin/inquiries" data-testid="admin-nav-inquiries" className="btn-secondary">
              <MessageSquare size={14} /> Inquiries
            </Link>
            <Link to="/admin/distributor-applications" data-testid="admin-nav-distributor-applications" className="btn-secondary">
              <Briefcase size={14} /> Distributor Apps
            </Link>
            <Link to="/admin/analytics" data-testid="admin-nav-analytics" className="btn-secondary">
              <BarChart3 size={14} /> Analytics
            </Link>
            <button
              onClick={doPing}
              disabled={pinging}
              data-testid="admin-ping-sitemap"
              title="Notify Bing, Yandex, Seznam, Naver & Yep (IndexNow). Google must be done via Search Console."
              className="btn-secondary disabled:opacity-60"
            >
              {pinging ? <Loader2 size={14} className="animate-spin" /> : <Radio size={14} />}
              {pinging ? "Pinging" : "Ping Search Engines"}
            </button>
            <button
              onClick={() => setShowChangePw(true)}
              data-testid="admin-change-password"
              className="btn-secondary"
            >
              <KeyRound size={14} /> Change Password
            </button>
            <button onClick={logout} data-testid="admin-logout" className="btn-secondary">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-brand-green/15 border border-brand-green/15 mb-8">
          {[
            { icon: FileText, l: "Total Posts", v: stats.total },
            { icon: BookCheck, l: "Published", v: stats.published },
            { icon: FileEdit, l: "Drafts", v: stats.drafts },
            { icon: Clock, l: "Total Read Time", v: `${stats.totalRead} min` },
          ].map((s) => (
            <div key={s.l} className="bg-white p-6">
              <s.icon size={14} className="text-brand-teal" />
              <p className="font-display text-3xl text-brand-black mt-3">{s.v}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/50 mt-1">{s.l}</p>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="bg-white border border-brand-green/15 p-4 mb-4 flex items-center gap-3">
          <Search size={14} className="text-brand-black/40" />
          <input
            type="text"
            placeholder="Search by title or tag..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-search"
            className="flex-1 bg-transparent text-sm focus:outline-none"
          />
        </div>

        {/* Bulk action toolbar — appears when 1+ rows selected */}
        <div
          data-testid="admin-bulk-toolbar"
          className={`overflow-hidden transition-all duration-300 ${selected.size > 0 ? "max-h-24 mb-4" : "max-h-0"}`}
          aria-hidden={selected.size === 0}
        >
          <div className="bg-brand-black text-brand-cream border border-brand-gold/40 p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand-gold">
                {selected.size} selected
              </span>
              <button
                onClick={clearSelection}
                data-testid="admin-bulk-clear"
                className="text-[11px] uppercase tracking-widest text-brand-cream/60 hover:text-brand-cream"
              >
                Clear
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => runBulk("publish")}
                disabled={bulkBusy}
                data-testid="admin-bulk-publish"
                className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium uppercase tracking-[0.18em] bg-brand-green hover:bg-brand-cream hover:text-brand-black transition disabled:opacity-50"
              >
                {bulkBusy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                Publish
              </button>
              <button
                onClick={() => runBulk("unpublish")}
                disabled={bulkBusy}
                data-testid="admin-bulk-unpublish"
                className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium uppercase tracking-[0.18em] border border-brand-cream/30 text-brand-cream hover:border-brand-gold hover:text-brand-gold transition disabled:opacity-50"
              >
                {bulkBusy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                Unpublish
              </button>
              <button
                onClick={() => setConfirmBulkDelete(true)}
                disabled={bulkBusy}
                data-testid="admin-bulk-delete"
                className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium uppercase tracking-[0.18em] bg-red-700 text-white hover:bg-red-800 transition disabled:opacity-50"
              >
                <Trash2 size={13} /> Delete
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-brand-green/15 overflow-x-auto">
          <table className="clinical-table">
            <thead>
              <tr>
                <th className="w-10">
                  <button
                    onClick={toggleAll}
                    aria-label="Select all on page"
                    data-testid="admin-select-all"
                    className="text-brand-black/60 hover:text-brand-teal transition"
                  >
                    {allOnPageSelected ? <CheckSquare size={15} /> : <Square size={15} />}
                  </button>
                </th>
                <th onClick={() => toggleSort("title")} className="cursor-pointer">Title</th>
                <th>Category</th>
                <th>Status</th>
                <th onClick={() => toggleSort("publishedAt")} className="cursor-pointer">Published</th>
                <th>Read Time</th>
                <th>Featured</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center text-brand-black/50">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-brand-black/50">No posts.</td></tr>
              ) : filtered.map((p) => {
                const color = CATEGORY_COLOR[p.category]?.hex || "#1a4d2e";
                const isSel = selected.has(p.id);
                return (
                  <tr key={p.id} data-testid={`admin-row-${p.id}`} className={isSel ? "bg-brand-teal/5" : ""}>
                    <td>
                      <button
                        onClick={() => toggleOne(p.id)}
                        aria-label={isSel ? "Deselect post" : "Select post"}
                        data-testid={`admin-select-${p.id}`}
                        className={`transition ${isSel ? "text-brand-teal" : "text-brand-black/40 hover:text-brand-teal"}`}
                      >
                        {isSel ? <CheckSquare size={15} /> : <Square size={15} />}
                      </button>
                    </td>
                    <td className="max-w-xs">
                      <p className="font-medium text-brand-black line-clamp-1">{p.title}</p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/40 mt-0.5">/blog/{p.slug}</p>
                    </td>
                    <td>
                      <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border" style={{ color, borderColor: `${color}40`, backgroundColor: `${color}10` }}>
                        {p.category}
                      </span>
                    </td>
                    <td>
                      <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${p.isPublished ? "text-brand-green bg-brand-green/5 border-brand-green/30" : "text-[#8a6e1f] bg-brand-gold/10 border-brand-gold/40"}`}>
                        {p.isPublished ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td className="font-mono text-xs">{new Date(p.publishedAt).toLocaleDateString()}</td>
                    <td className="font-mono text-xs">{p.readingTime} min</td>
                    <td>{p.isFeatured ? "★" : "—"}</td>
                    <td>
                      <div className="flex items-center gap-3">
                        <Link to={`/admin/posts/edit/${p.id}`} title="Edit" aria-label="Edit" data-testid={`admin-edit-${p.id}`} className="text-brand-teal hover:text-brand-green">
                          <Pencil size={14} />
                        </Link>
                        <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" title="Preview" aria-label="Preview" className="text-brand-black/60 hover:text-brand-black">
                          <Eye size={14} />
                        </a>
                        <button onClick={() => setConfirmDelete(p)} title="Delete" aria-label="Delete" data-testid={`admin-delete-${p.id}`} className="text-brand-black/60 hover:text-red-700">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-brand-black/45 mt-8 font-mono">
          Note: This admin panel uses client-side authentication suitable for MVP/demo use. Before going live, replace with server-side authentication (NextAuth, Supabase Auth, or similar).
        </p>
      </div>

      <ChangePasswordModal open={showChangePw} onClose={() => setShowChangePw(false)} />

      {confirmDelete && (
        <div className="fixed inset-0 bg-brand-black/60 z-50 flex items-center justify-center p-6" data-testid="admin-confirm-delete">
          <div className="bg-white max-w-md w-full p-8 border border-brand-green/15">
            <h2 className="font-display text-2xl text-brand-black">Delete "{confirmDelete.title.slice(0, 50)}{confirmDelete.title.length > 50 ? "..." : ""}"?</h2>
            <p className="text-sm text-brand-black/70 mt-3 leading-relaxed">This cannot be undone. The URL <span className="font-mono text-brand-teal">/blog/{confirmDelete.slug}</span> will return 404.</p>
            <div className="flex justify-end gap-3 mt-8">
              <button onClick={() => setConfirmDelete(null)} className="btn-secondary">Cancel</button>
              <button onClick={doDelete} data-testid="admin-confirm-delete-btn" className="inline-flex items-center justify-center gap-2 bg-red-700 text-white px-7 py-3.5 text-sm font-medium tracking-wide hover:bg-red-800 transition">
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmBulkDelete && (
        <div className="fixed inset-0 bg-brand-black/60 z-50 flex items-center justify-center p-6" data-testid="admin-confirm-bulk-delete">
          <div className="bg-white max-w-md w-full p-8 border border-brand-green/15">
            <h2 className="font-display text-2xl text-brand-black">Delete {selected.size} post{selected.size === 1 ? "" : "s"}?</h2>
            <p className="text-sm text-brand-black/70 mt-3 leading-relaxed">
              This permanently removes the selected post{selected.size === 1 ? "" : "s"} and breaks any inbound SEO links. Cannot be undone.
            </p>
            <div className="flex justify-end gap-3 mt-8">
              <button onClick={() => setConfirmBulkDelete(false)} disabled={bulkBusy} className="btn-secondary">Cancel</button>
              <button
                onClick={() => runBulk("delete")}
                disabled={bulkBusy}
                data-testid="admin-confirm-bulk-delete-btn"
                className="inline-flex items-center justify-center gap-2 bg-red-700 text-white px-7 py-3.5 text-sm font-medium tracking-wide hover:bg-red-800 transition disabled:opacity-60"
              >
                {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Delete {selected.size} Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
